import Link from 'next/link';
import { IEvent } from '@/lib/types';
import MapPinIcon from '@/assets/icon/map-pin.svg';
import ClockIcon from '@/assets/icon/clock.svg';
import GlobeIcon from '@/assets/icon/globe.svg';
import ArrowRightIcon from '@/assets/icon/arrow-right.svg';
import { SupportedLanguages } from '@/types/globals';
import CalendarIcon from '@/assets/icon/calendar.svg';

interface RegularEventCardProps {
  event: IEvent;
  basePath: string;
  formatDateLocal: (dateString: string) => {
    year: string;
    day: string;
    month: string;
  };
  formatTimeLocal: (timeString: string) => string;
  acfText: {
    seeDetails: string;
  };
  locale: SupportedLanguages;
}

const text = {
  en: {
    eventWebsite: 'Event Website',
  },
  fr: {
    eventWebsite: "Site de l'événement",
  },
};

export function RegularEventCard({
  event,
  basePath,
  formatDateLocal,
  formatTimeLocal,
  acfText,
  locale,
}: RegularEventCardProps) {
  // Helper function to render event details
  const renderEventDetails = (
    event: IEvent,
    iconColor: string = 'text-teal-500',
    textColor: string = 'text-qf-black',
  ) => (
    <div className="space-y-3">
      {event.acf?.address && (
        <div className="flex items-start gap-3">
          <MapPinIcon className={`h-4 w-4 ${iconColor} mt-0.5`} />
          <div
            className={`${textColor} font-inter text-[16px] leading-[20px] font-normal tracking-[-2%]`}
          >
            <div
              className="font-bold"
              dangerouslySetInnerHTML={{
                __html: event.acf.address.split('<br />')[0],
              }}
            />
            {event.acf.address.includes('<br />') && (
              <div
                className="text-gray-600"
                dangerouslySetInnerHTML={{
                  __html: event.acf.address
                    .split('<br />')
                    .slice(1)
                    .join('<br />'),
                }}
              />
            )}
          </div>
        </div>
      )}

      {event.acf?.event_date_end && (
        <div className="flex items-center gap-3">
          <CalendarIcon className={`h-4 w-4 ${iconColor}`} />
          <span
            className={`${textColor} font-inter text-[16px] leading-[20px] font-normal tracking-[-2%]`}
          >
            {formatDateLocal(event.acf.event_date_start).month}{' '}
            {formatDateLocal(event.acf.event_date_start).day} -{' '}
            {formatDateLocal(event.acf.event_date_end).month}{' '}
            {formatDateLocal(event.acf.event_date_end).day}
          </span>
        </div>
      )}

      {event.acf?.start_time && event.acf?.end_time && (
        <div className="flex items-center gap-3">
          <ClockIcon className={`h-4 w-4 ${iconColor}`} />
          <span
            className={`${textColor} font-inter text-[16px] leading-[20px] font-normal tracking-[-2%]`}
          >
            {formatTimeLocal(event.acf.start_time)} -{' '}
            {formatTimeLocal(event.acf.end_time)}
          </span>
        </div>
      )}

      {event.acf?.event_website && (
        <div className="flex items-center gap-3">
          <GlobeIcon className={`h-4 w-4 ${iconColor}`} />
          <Link
            href={event.acf.event_website}
            target="_blank"
            rel="noopener noreferrer"
            className={`${textColor} font-inter text-[16px] leading-[20px] font-normal tracking-[-2%] underline hover:text-teal-800`}
          >
            {text[locale].eventWebsite}
          </Link>
        </div>
      )}
    </div>
  );

  return (
    <div className="overflow-hidden rounded-lg">
      <div className="flex flex-col lg:flex-row">
        {/* Event Details */}
        <div className="p-6 lg:w-3/5">
          <div className="text-qf-black font-lora mb-4 text-[26px] leading-[130%] font-bold tracking-[-1.4%]">
            <span dangerouslySetInnerHTML={{ __html: event.title.rendered }} />
          </div>
          {event.acf?.event_details && (
            <p className="text-graphite-700 font-inter mb-6 [display:-webkit-box] overflow-hidden leading-relaxed [-webkit-box-orient:vertical] [-webkit-line-clamp:3]">
              <span
                dangerouslySetInnerHTML={{
                  __html: event.acf?.event_details.replace(/<[^>]*>/g, ''),
                }}
              />
            </p>
          )}

          <Link
            href={`/${basePath}/${event.slug}`}
            className="font-source-sans-pro inline-flex items-center gap-2.5 text-center align-middle text-[15px] leading-[20px] font-semibold tracking-[0%] text-teal-500 hover:text-teal-800"
          >
            {acfText.seeDetails}
            <ArrowRightIcon className="h-4 w-4" />
          </Link>
        </div>

        {/* Event Info Sidebar */}
        <div className="bg-mist-700 p-6 lg:w-2/5">
          <div className="flex items-start gap-4">
            {/* Date */}
            {event.acf?.event_date_start && (
              <div className="h-[87px] w-[79px] flex-shrink-0 rounded-[11px] bg-teal-500 pt-2 pr-6 pb-2 pl-6 text-center">
                <div className="font-inter text-2xl text-[14px] leading-[130%] font-medium tracking-[-1.4%] text-white">
                  {formatDateLocal(event.acf.event_date_start).year}
                </div>
                <div className="font-lora text-2xl text-[28px] leading-[130%] font-bold tracking-[-1.4%] text-white">
                  {formatDateLocal(event.acf.event_date_start).day}
                </div>
                <div className="font-inter text-sm text-[14px] leading-[130%] font-medium tracking-[-1.4%] text-white">
                  {formatDateLocal(event.acf.event_date_start).month}
                </div>
              </div>
            )}

            {/* Event Details */}
            <div className="flex-1">
              {renderEventDetails(event, 'text-teal-500', 'text-qf-black')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
